import { useMemo, useState } from "react";
import { Accordion, Box, Divider, Badge, Button, Grid, Group, List, Paper, SimpleGrid, Table, Text, ThemeIcon, Title } from "@mantine/core";
import {
  IconCalendarEvent,
  IconClockHour4,
  IconMapPin,
  IconMountain,
  IconRoute2,
  IconUsers,
} from "@tabler/icons-react";
import classes from "./CoverStoryView.module.css";

const formatAgeRange = (data) => {
  const min = data?.age_min ?? data?.cover_age_min;
  const max = data?.age_max ?? data?.cover_age_max;
  if (min != null && max != null) return `${min} - ${max} years`;
  return null;
};

const formatDuration = (data) => {
  const min = data?.min_duration ?? data?.cover_min_duration;
  const max = data?.max_duration ?? data?.cover_max_duration;
  if (min != null && max != null && String(min) !== String(max)) return `${min} - ${max} days`;
  if (max != null) return `${max} days`;
  return null;
};

const formatDate = (value) => {
  if (!value) return "NA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getArrayDisplay = (value, fallback = "NA") => {
  if (!value) return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  return String(value);
};

const resolveMediaUrl = (mediaPath) => {
  if (!mediaPath) return null;
  if (mediaPath.startsWith("http://") || mediaPath.startsWith("https://")) return mediaPath;
  return `${import.meta.env.VITE_API_ENDPOINT || ""}${mediaPath}`;
};

const chunk = (items, size) => {
  const groups = [];
  for (let i = 0; i < items.length; i += size) groups.push(items.slice(i, i + size));
  return groups;
};

const normalizeList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return [];
};

const WHY_CARD_TITLES = [
  "Adventure Specialists",
  "Fastest Response",
  "Comprehensive Network",
];

const CoverStoryView = ({ data }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [showAllBenefitRows, setShowAllBenefitRows] = useState(false);

  const heroTitle = data?.title || "Cover Plan";
  const subtitle = data?.subtitle || `${data?.cover_scope || "Global scope"} with curated mountain protection`;
  const mediaSrc = resolveMediaUrl(data?.brochure);
  const routeMapSrc = resolveMediaUrl(data?.route_map);
  const isPdfMedia = mediaSrc?.toLowerCase().includes(".pdf");
  const discountLabel = data?.discount_label?.trim() || "";
  const promoBanner = data?.promo_banner?.trim() || "";
  const showPromoBanner = Boolean(discountLabel || promoBanner);

  const infoCards = [
    { icon: IconMapPin, value: getArrayDisplay(data?.travelling_to_names || data?.cover_scope, "Worldwide") },
    { icon: IconMountain, value: data?.max_altitude ? `${data.max_altitude} m (Max. altitude)` : `${data?.max_duration || "NA"} days coverage` },
    { icon: IconRoute2, value: getArrayDisplay(data?.adventure_names || data?.type, "Himalayan trekking") },
    { icon: IconUsers, value: formatAgeRange(data) || "All travellers" },
    { icon: IconCalendarEvent, value: data?.season_window || `${formatDate(data?.start_date)} - ${formatDate(data?.end_date)}` },
    { icon: IconClockHour4, value: formatDuration(data) || "Flexible duration" },
  ];

  const keyFeatures = normalizeList(data?.key_features);

  const overviewFields = useMemo(
    () => [
      { label: "Duration", value: formatDuration(data) || "NA" },
      { label: "Age Range", value: formatAgeRange(data) || "NA" },
      { label: "Altitude Band", value: data?.altitude_band || "NA" },
      { label: "Difficulty", value: data?.difficulty || "NA" },
      { label: "Scope", value: data?.scope_description || data?.cover_scope || "NA" },
      { label: "Season Window", value: data?.season_window || "NA" },
      { label: "Region", value: data?.region || "NA" },
      { label: "Access Point", value: data?.access_point || "NA" },
      { label: "Terrain", value: data?.terrain || "NA" },
    ],
    [data]
  );

  const itineraryRows = data?.itinerary?.length
    ? data.itinerary
    : [{ day: "Day 1", altitude: "NA", risk: "NA", medical: "NA", evacuation: "NA" }];
  const allBenefits = (data?.benefit_descriptions && data.benefit_descriptions.length
    ? data.benefit_descriptions
    : data?.terms_and_conditions && data.terms_and_conditions.length
    ? data.terms_and_conditions
    : []);
  const benefitTiles = (allBenefits.length ? allBenefits : keyFeatures).slice(0, 6);
  const visibleBenefitRows = showAllBenefitRows ? allBenefits : allBenefits.slice(0, 8);
  const termsList = data?.terms_and_conditions || [];
  const faqList = (data?.faq && data.faq.length ? data.faq : []).map((item) =>
    typeof item === "string" ? { question: item, answer: "Please contact support for detailed operational guidance." } : item
  );
  const visibleFaqs = showAllFaqs ? faqList : faqList.slice(0, 6);
  const pitchLines = String(data?.pitch_text || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  const whyBullets = normalizeList(data?.why_asc360_bullets);
  const whyHighlights = chunk(whyBullets, 3).slice(0, 3).map((items, index) => ({
    title: WHY_CARD_TITLES[index] || `Highlights ${index + 1}`,
    points: items,
  }));
  const whyStats = [
    { value: data?.stat_covered, label: "Covered" },
    { value: data?.stat_evacuation, label: "Air+Land Evacuation" },
    { value: data?.stat_wilderness, label: "Wilderness Medicine" },
    { value: data?.stat_settled_amount, label: "Settled Amount" },
  ].filter((item) => item.value);

  const comparisonRows = normalizeList(data?.comparison_rows);
  const comparisonHeader = useMemo(() => {
    const first = comparisonRows[0];
    if (!first?.competitor_values?.length) return [];
    return first.competitor_values.map((item, index) => item?.competitor_name || `Competitor ${index + 1}`);
  }, [comparisonRows]);

  const tabValues = useMemo(
    () => [
      { id: "overview", label: "Coverage Overview" },
      { id: "benefits", label: "Benefits Descriptions" },
      { id: "why", label: "Why ASC360" },
      { id: "terms", label: "Terms & Conditions" },
      { id: "faqs", label: "FAQs" },
    ],
    []
  );

  return (
    <Paper p="lg" radius="lg">
      <Group justify="space-between" gap="md" align="flex-start" wrap="wrap" bd="1px solid #dde8f2" p="md">
        <div>
          <Title c="#092C4C" order={3}>
            {heroTitle}
          </Title>
          <Text className={classes.subtitle}>{subtitle}</Text>
        </div>  
        {discountLabel ? (
          <Badge className={classes.offerBadge} radius="xl">
            {discountLabel}
          </Badge>
        ) : null}
      </Group>
      <Divider  mb="lg" />

      <Grid gutter={14} align="stretch" className={classes.heroGrid}>
        <Grid.Col span={{ base: 12, md: 9 }} className={classes.firstLeftColumn}>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {infoCards.map((item, index) => (
              <Paper key={index} className={classes.infoCard} radius="md" p="sm">
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="white" color="blue" size={36} radius="md">
                    <item.icon size={20} />
                  </ThemeIcon>
                  <Text className={classes.infoValue} fw={500} >{item.value}</Text>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>

          <Paper className={classes.pitchBox} radius="md" p="lg" mt="lg">
            <Title order={4} mb="md" className={classes.pitchTitle}>
              Your Pitch
            </Title>
            <Divider mb="md" />
            <Text className={classes.pitchText} size="sm">
              {pitchLines.length ? pitchLines[0] : "Pitch details are available in this plan."}
            </Text>
            {pitchLines.slice(1).map((line, index) => (
              <Text key={`${line}-${index}`} className={classes.pitchText} size="sm">
                {line}
              </Text>
            ))}
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 3 }} className={classes.firstRightColumn}>
          <Paper className={classes.sidePanel} radius="md" p="md">
            <div className={classes.brochureBox}>
              {mediaSrc && !isPdfMedia ? (
                <img src={mediaSrc} alt="Plan brochure preview" className={classes.brochureImage} />
              ) : mediaSrc && isPdfMedia ? (
                <div className={classes.previewPlaceholder}>
                  <Text fw={700} c="#092C4C">Brochure PDF</Text>
                  <Button component="a" href={mediaSrc} target="_blank" rel="noreferrer" mt="xs" variant="light">
                    Open File
                  </Button>
                </div>
              ) : (
                <div className={classes.previewPlaceholder}>
                  <Text fw={700} c="#092C4C">{data?.token || "PLAN"}</Text>
                  <Text size="xs" c="dimmed">Brochure preview unavailable</Text>
                </div>
              )}
            </div>

            <Button
              classNames={{ root: classes.secondaryBtn, label: classes.secondaryBtnLabel }}
              variant="outline"
              fullWidth
              mt="md"
              component="a"
              href="/operator/operators/create"
              target="_blank"
              rel="noopener noreferrer"
            >
              Onboard New Operator
            </Button>
          </Paper>
        </Grid.Col>
      </Grid>

      {showPromoBanner ? (
        <Paper bg="#2f88de" radius="none" p="md" mt="lg">
          <Text c="#fff" fw={500} ta="center">
            {discountLabel ? (
              <Text span fw={700}>
                {discountLabel}
                {promoBanner ? " " : ""}
              </Text>
            ) : null}
            {promoBanner ? <Text span>{promoBanner}</Text> : null}
          </Text>
        </Paper>
      ) : null}

      <Group className={classes.tabs} gap={0}>
      {tabValues.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`${classes.tabBtn} ${activeTab === tab.id ? classes.tabBtnActive : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </Group>
      <Divider />

      {activeTab === "overview" && (
        <Box className={classes.overviewSection}>
          <Grid className={classes.overviewRow} gutter="lg" align="flex-start">
            <Grid.Col span={{ base: 12, md: 8 }}>
              <div className={classes.overviewDetails}>
                {overviewFields.map((field) => (
                  <div key={field.label} className={classes.detailRow}>
                    <Text span className={classes.detailLabel}>
                      {field.label} :
                    </Text>
                    <Text span className={classes.detailValue}>
                      {field.value}
                    </Text>
                  </div>
                ))}
              </div>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <div className={classes.mapWrap}>
                {routeMapSrc ? (
                  <img src={routeMapSrc} alt="Coverage route map" className={classes.overviewMapImage} />
                ) : (
                  <div className={classes.mapPlaceholder}>Route Map</div>
                )}
              </div>
            </Grid.Col>
          </Grid>

          <Box className={classes.keyFeaturesSection}>
            <Title order={4} className={classes.keyFeaturesTitle}>
              Key Features :
            </Title>
            <ul className={classes.keyFeaturesList}>
              {(keyFeatures.length ? keyFeatures : allBenefits).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Box>
        </Box>
      )}

      {activeTab === "benefits" && (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md" mt="md">
            {benefitTiles.map((item) => (
              <Paper key={item} bg="#F3F8FE" radius="sm" p="md">
                <Text variant="gradient" gradient={{ from: "#0053A8", to: "#033F7C", deg: 270 }} fw={500} fz={14}>
                  {item}
                </Text>

              </Paper>
            ))}
          </SimpleGrid>

          <Paper radius="md" mt="md" withBorder style={{ overflow: "hidden" }}>
            <Box
              bg="linear-gradient(270deg, #0053A8 0%, #033F7C 100%)"
              px="md"
              py="sm"
            >
              <Title order={5} c="#fff" ta="center" fw={600}>
                Services Itinerary
              </Title>
            </Box>
            <Box p="md">
              <Table.ScrollContainer minWidth={960}>
              <Table
                withColumnBorders
                highlightOnHover
                striped
                styles={{
                  thead: { backgroundColor: "#8fb2d9" },
                  th: {
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    borderColor: "#dbe7f3",
                    whiteSpace: "nowrap",
                  },
                  td: {
                    color: "#5f7f9e",
                    fontSize: 12,
                    fontWeight: 500,
                    verticalAlign: "top",
                    borderColor: "#e3edf7",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  },
                }}
              >
            <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 180 }}>Location</Table.Th>
                  <Table.Th style={{ minWidth: 90 }}>Altitude</Table.Th>
                  <Table.Th style={{ minWidth: 160 }}>Risk</Table.Th>
                  <Table.Th style={{ minWidth: 160 }}>Medical Facility</Table.Th>
                  <Table.Th style={{ minWidth: 160 }}>Evacuation</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {itineraryRows.map((row, index) => (
                  <Table.Tr key={`${row.day}-${index}`}>
                    <Table.Td>{row.day}</Table.Td>
                    <Table.Td>{row.altitude}</Table.Td>
                    <Table.Td>{row.risk}</Table.Td>
                    <Table.Td>{row.medical}</Table.Td>
                    <Table.Td>{row.evacuation}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Box>
          </Paper>

          <Paper className={classes.section} radius="md" mt="md" p="md" >
            <Accordion
              variant="separated"
              classNames={{
                item: classes.benefitItem,
                control: classes.benefitControl,
                panel: classes.benefitPanel,
              }}
              defaultValue="benefit-0"
            >
              {(visibleBenefitRows.length ? visibleBenefitRows : ["No benefit descriptions available"]).map((item, index) => (
                <Accordion.Item key={`${item}-${index}`} value={`benefit-${index}`}>
                  <Accordion.Control>{item}</Accordion.Control>
                  <Accordion.Panel>
                    <Text>
                      Hospitalization expenses are reimbursed as per approved limits and policy wording.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>

            {allBenefits.length > 8 && (
              <Group justify="center" mt="lg">
                <Button
                  variant="outline"
                  className={classes.viewMoreBtn}
                  onClick={() => setShowAllBenefitRows((prev) => !prev)}
                >
                  {showAllBenefitRows ? "View Less" : "View more"}
                </Button>
              </Group>
            )}
          </Paper>
        </>
      )}

      {activeTab === "why" && (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md" mt="lg">
            {(whyHighlights.length ? whyHighlights : []).map((block) => (
              <Paper key={block.title} bg="#F3F8FE" radius="md" p="md">
                <Text fw={600} fz={14} variant="gradient" gradient={{ from: "#0053A8", to: "#033F7C", deg: 270 }}>{block.title}</Text>
                <List spacing={8} mt="sm" c="#6C94B4" fz={14}>
                  {block.points.map((point) => (
                    <List.Item key={point}>{point}</List.Item>
                  ))}
                </List>
              </Paper>
            ))}
          </SimpleGrid>

          <SimpleGrid cols={{ base: 2, sm: 2, lg: 4 }} spacing="md" mt="lg">
            {(whyStats.length ? whyStats : [{ value: "NA", label: "Coverage stats" }]).map((stat) => (
              <Paper key={stat.label} className={classes.statCard} radius="md" p="md" bg="#fff" style={{boxShadow: "0 2px 8px 0 rgba(122, 167, 219, 0.24)"}}>
                <Text fz={24} fw={600} variant="gradient" gradient={{ from: "#FF6528", to: "#E78600 ", deg: 92 }}>{stat.value}</Text>
                <Text className={classes.statLabel}>{stat.label}</Text>
              </Paper>
            ))}
          </SimpleGrid>

          <Paper className={classes.section} radius="sm" mt="lg" p="lg" bg="#F3F8FE">
            <Title order={4} fw={600} fz={14} variant="gradient" gradient={{ from: "#0053A8", to: "#033F7C", deg: 270 }}>Why choose ASC360</Title>
            <Text className={classes.whyDesc} mt="sm">
              {data?.why_asc360_text || "Explore why this plan is suitable for your journey."}
            </Text>
            <List spacing="sm" mt="md" className={classes.featureList}>
              {whyBullets.map((bullet) => (
                <List.Item key={bullet}>{bullet}</List.Item>
              ))}
            </List>
          </Paper>

          <Paper className={classes.section} radius="md" mt="md" p={0} withBorder>
            <div className={classes.comparisonHeader}>FN - Plan Comparison</div>
            <Table withColumnBorders highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Coverage</Table.Th>
                  {comparisonHeader.map((header) => (
                    <Table.Th key={header}>{header}</Table.Th>
                  ))}
                  <Table.Th>ASC360</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(comparisonRows.length ? comparisonRows : [{ coverage_label: "No comparison rows", asc360_value: "NA", competitor_values: [] }]).map((row, rowIndex) => (
                  <Table.Tr key={`${row?.coverage_label || "row"}-${rowIndex}`}>
                    <Table.Td>{row?.coverage_label || "Coverage"}</Table.Td>
                    {comparisonHeader.map((_, idx) => (
                      <Table.Td key={`${rowIndex}-${idx}`}>
                        {row?.competitor_values?.[idx]?.value || "NA"}
                      </Table.Td>
                    ))}
                    <Table.Td className={classes.lastCol}>{row?.asc360_value || "NA"}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </>
      )}

      {activeTab === "terms" && (
        <Paper className={classes.section} radius="md" mt="md" p="md">
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper bg="#E7F6EA" radius="md" p="lg" h="100%" style={{ display: "flex", flexDirection: "column" }}>
                <Text
                  component="h4"
                  fw={500}
                  style={{
                    margin: 0,
                    background: "linear-gradient(270deg, #0053A8 0%, #033F7C 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Important Notes :
                </Text>
                <List spacing={8} mt="sm" c="#6C94B4" fz={14} fw={500}>
                  {(data?.important_notes?.length ? data.important_notes : ["No important notes available"]).map((item) => (
                    <List.Item key={item}>{item}</List.Item>
                  ))}
                </List>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper bg="#FEF4E7" radius="md" p="lg" h="100%" style={{ display: "flex", flexDirection: "column" }}>
                <Text
                  component="h4"
                  fw={500}
                  style={{
                    margin: 0,
                    background: "linear-gradient(270deg, #0053A8 0%, #033F7C 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Conditional Coverage :
                </Text>
                <List spacing={8} mt="sm" c="#6C94B4" fz={14} fw={500}>
                  {(data?.conditional_coverage?.length ? data.conditional_coverage : ["No conditional coverage notes available"]).map((item) => (
                    <List.Item key={item}>{item}</List.Item>
                  ))}
                </List>
              </Paper>
            </Grid.Col>
          </Grid>

          <Text
            component="h4"
            fw={500}
            mt="md"
            style={{
              margin: 0,
              background: "linear-gradient(270deg, #0053A8 0%, #033F7C 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Terms & Conditions :</Text>
          <List spacing="sm" mt="sm" className={classes.termsList}>
            {(termsList.length ? termsList : ["No terms and conditions available"]).map((item, index) => (
              <List.Item key={`${item}-${index}`}>{item}</List.Item>
            ))}
          </List>
        </Paper>
      )}

      {activeTab === "faqs" && (
        <Paper radius="md" mt="md" p="md" withBorder>
          <Accordion
            variant="separated"
            defaultValue="faq-0"
            styles={{
              item: {
                border: "1px solid #dbe7f3",
                borderRadius: 8,
                overflow: "hidden",
                background: "#fff",
                marginBottom: 10,
              },
              control: {
                color: "#0d4b86",
                fontSize: 16,
                fontWeight: 600,
                padding: "14px 16px",
              },
              panel: {
                background: "#f1f6fb",
                borderTop: "none",
                color: "#5f7f9e",
                fontSize: 14,
                padding: "12px 16px 14px",
              },
              chevron: {
                color: "#7ea0bf",
              },
            }}
          >
            {visibleFaqs.map((item, index) => (
              <Accordion.Item key={`${item.question}-${index}`} value={`faq-${index}`}>
                <Accordion.Control>{item.question}</Accordion.Control>
                <Accordion.Panel>
                  <Text>{item.answer || "Please contact support for detailed operational guidance."}</Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>

          {faqList.length > 6 && (
            <Group justify="center" mt="lg">
              <Button
                variant="outline"
                onClick={() => setShowAllFaqs((prev) => !prev)}
                styles={{
                  root: {
                    borderColor: "#0c4d8d",
                    color: "#0c4d8d",
                    fontWeight: 700,
                    minWidth: 112,
                  },
                }}
              >
                {showAllFaqs ? "View Less" : "View more"}
              </Button>
            </Group>
          )}
        </Paper>
      )}
    </Paper>
  );
};

export default CoverStoryView;